import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { Dialog } from '@components/dialogs/Dialog';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { Loader } from '@components/feed/Loader';
import { RankingTable } from '@pages/Portal/Competitions/components/RankingTable';
import { useGetPortalCompetitionSessionRanking } from '@reactQuery/competitions';
import { useAuth } from '@context/AuthProvider';
import { useTranslation } from 'react-i18next';
import { formatLocaleDateAtTime } from '@helpers/dateHelpers';
import {
  getRankingValueColumnLabel,
  mapPortalRankingEntryToRankingItem,
} from '../../../helpers';
import { ISessionRankingDialog } from './interfaces';

const SESSION_RANKING_DIALOG_MIN_WIDTH = 'clamp(320px, calc(100vw - 32px), 960px)';

export const SessionRankingDialog: React.FC<ISessionRankingDialog> = ({
  show,
  slug,
  session,
  variant,
  onClose,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const highlightedMemberSlug = currentUser?.member?.slug;
  const sessionId = session?.id;

  const { data: ranking, isLoading } = useGetPortalCompetitionSessionRanking(
    slug,
    sessionId,
    variant,
    show && session?.status === 'completed'
  );

  const rankingItems = useMemo(
    () => ranking?.data.map(mapPortalRankingEntryToRankingItem) ?? [],
    [ranking]
  );

  const rankingScoreColumnLabel = useMemo(
    () =>
      ranking?.meta.rankingMethod && ranking.meta.scoreUnit
        ? getRankingValueColumnLabel(
            ranking.meta.scoreUnit,
            ranking.meta.rankingMethod
          )
        : undefined,
    [ranking?.meta.rankingMethod, ranking?.meta.scoreUnit]
  );

  if (!session) {
    return null;
  }

  const dialogTitle = t('competitions:portal.details.sessionRankingDialog.title', {
    date: formatLocaleDateAtTime(session.heldOn),
  });

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return (
        <Box py={4}>
          <Loader />
        </Box>
      );
    }

    if (rankingItems.length === 0) {
      return (
        <EmptyPlaceholder
          icon={LeaderboardIcon}
          title={t(
            'competitions:portal.details.sessionRankingDialog.empty.title'
          )}
          subTitle={t(
            'competitions:portal.details.sessionRankingDialog.empty.subTitle'
          )}
        />
      );
    }

    return (
      <RankingTable
        rankingData={rankingItems}
        submittedCountColumnLabel={t(
          'competitions:portal.details.sessionRankingDialog.columns.seriesCount'
        )}
        scoreColumnLabel={rankingScoreColumnLabel}
        disableColumnSorting
        highlightedMemberSlug={highlightedMemberSlug}
      />
    );
  };

  return (
    <Dialog
      show={show}
      title={dialogTitle}
      minWidth={SESSION_RANKING_DIALOG_MIN_WIDTH}
      maxWidth={false}
      onClose={onClose}
    >
      {renderBody()}
    </Dialog>
  );
};
